import React, { useState, useEffect } from "react";
import { DashboardStyles } from "./components/DashboardStyles";
import { BackgroundEffects } from "./components/BackgroundEffects";
import { Sidebar, NavigationItem } from "./components/Sidebar";
import { Topbar } from "./components/Topbar";
import { KPIRow } from "./components/KPIRow";
import { DefaultRateChart } from "./components/DefaultRateChart";
import { ActivityFeed } from "./components/ActivityFeed";
import { LoanPerformanceTable } from "./components/LoanPerformanceTable";
import { RegionalRiskSnapshot } from "./components/RegionalRiskSnapshot";
import { LoanCohorts } from "./components/LoanCohorts";
import { RegionsRisk } from "./components/RegionsRisk";
import { IndustryData } from "./components/IndustryDataView";
import { Loan, Region, ActivityItem, KPIData } from "./types";
import {
  LoanStatisticsResponse,
  LoanStatisticsService,
  DelinquencyRateResponse,
} from "./services/loanStatisticsService";

const MortgageChainDashboard: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTimeframe, setActiveTimeframe] = useState("All");
  const [activeFilter, setActiveFilter] = useState("All");
  const [activeNav, setActiveNav] = useState<NavigationItem>("portfolio");
  const [loanStatistics, setLoanStatistics] =
    useState<LoanStatisticsResponse | null>(null);
  const [delinquencyRate, setDelinquencyRate] =
    useState<DelinquencyRateResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLoanStatistics = async () => {
      const statistics = await LoanStatisticsService.getLoanStatistics();
      setLoanStatistics(statistics);
    };
    fetchLoanStatistics();
  }, []);

  console.log("loanStatistics", loanStatistics);

  const loanData: Loan[] = [
    {
      id: "L-10847",
      amount: "$485,000",
      ltv: "75%",
      rate: "7.85%",
      location: "Toronto M5V",
      status: "current",
      dpd: 0,
    },
    {
      id: "L-10846",
      amount: "$625,000",
      ltv: "80%",
      rate: "8.25%",
      location: "Vancouver V6B",
      status: "current",
      dpd: 0,
    },
    {
      id: "L-10845",
      amount: "$375,000",
      ltv: "70%",
      rate: "7.50%",
      location: "Calgary T2P",
      status: "late",
      dpd: 32,
    },
    {
      id: "L-10844",
      amount: "$550,000",
      ltv: "78%",
      rate: "8.10%",
      location: "Montreal H3B",
      status: "current",
      dpd: 0,
    },
    {
      id: "L-10843",
      amount: "$425,000",
      ltv: "72%",
      rate: "7.75%",
      location: "Toronto M4W",
      status: "default",
      dpd: 94,
    },
    {
      id: "L-10842",
      amount: "$750,000",
      ltv: "85%",
      rate: "8.75%",
      location: "Vancouver V5K",
      status: "current",
      dpd: 0,
    },
  ];

  const regionData: Region[] = [
    { name: "Toronto", value: "1.8%", loans: 847, ltv: 74, risk: "low" },
    { name: "Vancouver", value: "2.1%", loans: 623, ltv: 77, risk: "low" },
    { name: "Calgary", value: "3.4%", loans: 412, ltv: 79, risk: "medium" },
    { name: "Montreal", value: "2.3%", loans: 534, ltv: 76, risk: "low" },
    { name: "Ottawa", value: "1.9%", loans: 289, ltv: 71, risk: "low" },
    { name: "Edmonton", value: "3.7%", loans: 267, ltv: 80, risk: "medium" },
    { name: "Halifax", value: "4.1%", loans: 112, ltv: 81, risk: "high" },
    { name: "Winnipeg", value: "3.2%", loans: 178, ltv: 77, risk: "medium" },
    { name: "Hamilton", value: "2.5%", loans: 234, ltv: 75, risk: "low" },
  ];

  const activityFeed: ActivityItem[] = [
    {
      title: "New data upload",
      time: "2 minutes ago",
      detail: "127 loans ingested from Broker Network Inc.",
    },
    {
      title: "AI model updated",
      time: "15 minutes ago",
      detail: "October dataset processed with updated ML models.",
    },
    {
      title: "API query spike",
      time: "1 hour ago",
      detail: "PropTech Solutions requested cohort default curves.",
    },
    {
      title: "New investor workspace",
      time: "3 hours ago",
      detail: "Investment Fund Co. upgraded to Business tier.",
    },
    {
      title: "Benchmark report",
      time: "5 hours ago",
      detail: "Q4 Alternative Lending Benchmark exported to PDF.",
    },
  ];

  // Fetch loan statistics and delinquency rate on component mount
  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setIsLoading(true);
        setError(null);

        // Fetch both statistics and delinquency rate in parallel
        const [statisticsResponse, delinquencyRateResponse] = await Promise.all(
          [
            LoanStatisticsService.getLoanStatistics(),
            LoanStatisticsService.getDelinquencyRate(),
          ]
        );

        setLoanStatistics(statisticsResponse);
        setDelinquencyRate(delinquencyRateResponse);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to fetch loan data";
        setError(errorMessage);
        console.error("Error fetching loan data:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Format number with commas
  const formatNumber = (num: number): string => {
    return num.toLocaleString("en-US");
  };

  // Format currency
  const formatCurrency = (num: number): string => {
    if (num >= 1000000000) {
      return `$${(num / 1000000000).toFixed(1)}B`;
    } else if (num >= 1000000) {
      return `$${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `$${(num / 1000).toFixed(1)}K`;
    }
    return `$${num.toLocaleString("en-US")}`;
  };

  // Format percentage
  const formatPercentage = (num: number): string => {
    return `${num.toFixed(2)}%`;
  };

  // Generate KPI data from loan statistics
  const kpiData: KPIData[] = loanStatistics
    ? [
        {
          label: "Total active loans",
          tag: "Canada · All tiers",
          value: formatNumber(loanStatistics.data.totalActiveLoans),
          subtitle: `Total loans: ${formatNumber(
            loanStatistics.data.totalLoans
          )}`,
          change: {
            value: `${formatNumber(
              loanStatistics.data.totalLoans -
                loanStatistics.data.totalActiveLoans
            )} inactive`,
            type: "positive",
          },
          sparkline: {
            points:
              "0,20 10,18 20,19 30,15 40,17 50,12 60,13 70,9 80,10 90,7 100,6",
            stroke: "rgba(0,212,255,0.9)",
          },
        },
        {
          label: "Portfolio value",
          tag: "CAD · Mark-to-model",
          value: formatCurrency(loanStatistics.data.portfolioValue),
          subtitle: `Total principal: ${formatCurrency(
            loanStatistics.data.totalPrincipal
          )}`,
          change: {
            value: `Avg loan: ${formatCurrency(
              loanStatistics.data.averageLoanAmount
            )}`,
            type: "positive",
          },
          sparkline: {
            points:
              "0,25 8,23 16,22 24,20 32,18 40,16 48,14 56,13 64,12 72,11 80,10 88,9 96,8 100,7",
            stroke: "rgba(0,255,136,0.9)",
          },
        },
        {
          label: "Average default rate",
          tag: "Rolling 12 months",
          value: formatPercentage(loanStatistics.data.defaultRate * 100),
          subtitle: delinquencyRate
            ? `Delinquency rate: ${delinquencyRate.data.formatted}`
            : `Delinquency rate: ${formatPercentage(
                loanStatistics.data.delinquencyRate * 100
              )}`,
          change: {
            value: `${formatPercentage(
              loanStatistics.data.averageDefaultRate * 100
            )} average`,
            type:
              loanStatistics.data.defaultRate >
              loanStatistics.data.averageDefaultRate
                ? "negative"
                : "positive",
          },
          sparkline: {
            points:
              "0,10 10,9 20,11 30,13 40,12 50,14 60,16 70,17 80,19 90,20 100,21",
            stroke: "rgba(255,184,0,0.9)",
          },
        },
        {
          label: "Data partners",
          tag: "Coverage",
          value: loanStatistics.data.dataPartners.count.toString(),
          subtitle: `${loanStatistics.data.dataPartners.partners.length} active partners`,
          change: {
            value: `${loanStatistics.data.dataPartners.partners.length} partners`,
            type: "positive",
          },
          sparkline: {
            points: "0,24 12,23 24,22 36,20 48,18 60,16 72,15 84,14 96,13",
            stroke: "rgba(139,92,246,0.9)",
          },
        },
      ]
    : [
        {
          label: "Total active loans",
          tag: "Canada · All tiers",
          value: isLoading ? "Loading..." : error ? "Error" : "0",
          subtitle: isLoading ? "Fetching data..." : error || "No data",
          change: { value: "-", type: "positive" },
          sparkline: {
            points:
              "0,20 10,18 20,19 30,15 40,17 50,12 60,13 70,9 80,10 90,7 100,6",
            stroke: "rgba(0,212,255,0.9)",
          },
        },
        {
          label: "Portfolio value",
          tag: "CAD · Mark-to-model",
          value: isLoading ? "Loading..." : error ? "Error" : "$0",
          subtitle: isLoading ? "Fetching data..." : error || "No data",
          change: { value: "-", type: "positive" },
          sparkline: {
            points:
              "0,25 8,23 16,22 24,20 32,18 40,16 48,14 56,13 64,12 72,11 80,10 88,9 96,8 100,7",
            stroke: "rgba(0,255,136,0.9)",
          },
        },
        {
          label: "Average default rate",
          tag: "Rolling 12 months",
          value: isLoading ? "Loading..." : error ? "Error" : "0%",
          subtitle: isLoading ? "Fetching data..." : error || "No data",
          change: { value: "-", type: "positive" },
          sparkline: {
            points:
              "0,10 10,9 20,11 30,13 40,12 50,14 60,16 70,17 80,19 90,20 100,21",
            stroke: "rgba(255,184,0,0.9)",
          },
        },
        {
          label: "Data partners",
          tag: "Coverage",
          value: isLoading ? "Loading..." : error ? "Error" : "0",
          subtitle: isLoading ? "Fetching data..." : error || "No data",
          change: { value: "-", type: "positive" },
          sparkline: {
            points: "0,24 12,23 24,22 36,20 48,18 60,16 72,15 84,14 96,13",
            stroke: "rgba(139,92,246,0.9)",
          },
        },
      ];

  return (
    <>
      <DashboardStyles />
      <div className="min-h-screen bg-gradient-to-br from-[#15214a] via-[#050716] to-[#27133e] text-white relative overflow-x-hidden">
        <BackgroundEffects />

        {/* Mobile backdrop overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[15] lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        <div className="flex min-h-screen relative z-[1] max-lg:flex-col">
          <Sidebar
            isOpen={sidebarOpen}
            activeNav={activeNav}
            onNavChange={setActiveNav}
          />

          {/* MAIN CONTENT */}
          <main className="flex-1 ml-[20%] w-[80%] overflow-y-auto h-screen relative z-[1] max-lg:ml-0 max-lg:w-full max-lg:h-auto">
            {activeNav === "portfolio" && (
              <div className="p-6 pb-8 flex flex-col gap-6">
                <Topbar
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  onSidebarToggle={() => setSidebarOpen(!sidebarOpen)}
                />

                <KPIRow kpis={kpiData} />

                {/* MAIN GRID */}
                <section className="grid grid-cols-[3fr_1.4fr] gap-6 items-start xl:grid-cols-1">
                  <DefaultRateChart
                    activeTimeframe={activeTimeframe}
                    onTimeframeChange={setActiveTimeframe}
                  />
                  {/* <ActivityFeed activities={activityFeed} /> */}
                </section>

                {/* BOTTOM GRID */}
                <section className="grid grid-cols-[3fr_1.5fr] gap-6 xl:grid-cols-1">
                  <LoanPerformanceTable
                    loans={loanData}
                    activeFilter={activeFilter}
                    onFilterChange={setActiveFilter}
                  />
                  <RegionalRiskSnapshot regions={regionData} />
                </section>
              </div>
            )}

            {activeNav === "cohorts" && (
              <LoanCohorts
                onSidebarToggle={() => setSidebarOpen(!sidebarOpen)}
              />
            )}

            {activeNav === "industry" && (
              <IndustryData
                onSidebarToggle={() => setSidebarOpen(!sidebarOpen)}
              />
            )}

            {activeNav === "regions" && (
              <RegionsRisk
                onSidebarToggle={() => setSidebarOpen(!sidebarOpen)}
              />
            )}
          </main>
        </div>
      </div>
    </>
  );
};

export default MortgageChainDashboard;
